// apps/web/src/components/ZonedDateTimeField.tsx
//
// A Due-style date-time field that is both typeable and GUI-driven, with the
// timezone kept explicit.
//
//   * The field is an editable, masked input — type "06/15/2026 03:30 PM"
//     directly (numeric sections, arrow-key stepping). A short timezone
//     abbreviation (e.g. "PDT") sits in the read view.
//   * The icon opens a compact popover: timezone + OK on the top-right, then a
//     calendar and scrollable Hour / Min / AM-PM columns (no circular clock).
//     Typing and the GUI stay in sync because both drive one value.
//
// The value in/out is always a UTC ISO instant. The app's date-fns adapter has
// no IANA-timezone support, so the zone is managed here via "floating" Dates
// (local fields = the wall clock in the chosen zone) + the shared converters.

import { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { MultiSectionDigitalClock } from "@mui/x-date-pickers/MultiSectionDigitalClock";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { tzOffsetMinutes, utcToZonedWallClock, zonedWallClockToUtc } from "@manna/ui/date";

function clientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const ALL_ZONES: string[] =
  typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC"];

// Short timezone abbreviation: "PDT" / "EST" / "UTC" for common zones, compact
// "GMT±N" elsewhere. Computed at the given instant so DST is reflected.
function tzAbbrev(timeZone: string, at: Date): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

function offsetLabel(min: number): string {
  const sign = min < 0 ? "-" : "+";
  const a = Math.abs(min);
  return `GMT${sign}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

interface ZoneOption {
  id: string;
  label: string;
  offset: number;
}

// All IANA zones, labelled with offset + abbreviation and sorted by offset.
function buildZoneOptions(at: Date): ZoneOption[] {
  return ALL_ZONES.map((id) => {
    const offset = tzOffsetMinutes(id, at);
    const ab = tzAbbrev(id, at);
    const code = ab && !ab.startsWith("GMT") ? ` · ${ab}` : "";
    return { id, offset, label: `(${offsetLabel(offset)}) ${id}${code}` };
  }).sort((a, b) => a.offset - b.offset || a.id.localeCompare(b.id));
}

// UTC ISO → floating Date whose local fields are the wall clock in `timeZone`.
function utcToFloatingDate(value: string | null, timeZone: string): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(utcToZonedWallClock(value, timeZone));
  if (!m) return null;
  const [, y = "0", mo = "1", d = "1", h = "0", mi = "0"] = m;
  return new Date(+y, +mo - 1, +d, +h, +mi);
}

// Floating Date (wall clock in `timeZone`) → UTC ISO instant.
function floatingDateToUtc(d: Date | null, timeZone: string): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  const wall = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
  return zonedWallClockToUtc(wall, timeZone)?.toISOString() ?? null;
}

export interface ZonedDateTimeFieldProps {
  label: string;
  /** The current value as a UTC ISO instant, or null/"" when unset. */
  value: string | null;
  /** Emits the chosen instant as a UTC ISO string, or null when cleared. */
  onChange: (utcIso: string | null) => void;
  size?: "small" | "medium";
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /** Initial timezone; defaults to the viewer's local zone. */
  defaultTimeZone?: string;
}

export function ZonedDateTimeField({
  label,
  value,
  onChange,
  size = "small",
  disabled,
  sx,
  defaultTimeZone,
}: ZonedDateTimeFieldProps) {
  const [timeZone, setTimeZone] = useState(() => defaultTimeZone ?? clientTimeZone());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Empty entry defaults to the current date (so the year fills in as the
  // current year rather than staying blank).
  const referenceDate = useMemo(() => new Date(), []);
  const zoneOptions = useMemo(() => buildZoneOptions(new Date()), []);
  const selectedZone = zoneOptions.find((o) => o.id === timeZone) ?? {
    id: timeZone,
    label: timeZone,
    offset: 0,
  };

  const abbrev = tzAbbrev(timeZone, value ? new Date(value) : new Date());
  const pickerValue = utcToFloatingDate(value, timeZone);

  const handleChange = (d: Date | null) => onChange(floatingDateToUtc(d, timeZone));

  // Changing the zone keeps the wall clock the user already entered and
  // reinterprets it in the new zone (intuitive data-entry semantics).
  const handleZoneChange = (nextZone: string) => {
    setTimeZone(nextZone);
    if (value) onChange(floatingDateToUtc(utcToFloatingDate(value, timeZone), nextZone));
  };

  return (
    // Self-contained localization context so the field can be dropped anywhere
    // (and rendered in tests) without the caller wiring a LocalizationProvider.
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={sx} ref={fieldRef}>
        <DateTimeField
          label={label}
          value={pickerValue}
          onChange={handleChange}
          referenceDate={referenceDate}
          format="MM/dd/yyyy hh:mm a"
          size={size}
          fullWidth
          disabled={disabled}
          inputProps={{ "aria-label": label }}
          // Trim the input's right padding so the AM/PM section isn't clipped by
          // the abbreviation + icon adornment.
          sx={{ "& .MuiInputBase-input": { pr: 0 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>
                  {abbrev}
                </Typography>
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`Open ${label} picker`}
                  disabled={disabled}
                  onClick={() => setAnchorEl(fieldRef.current)}
                >
                  <EventIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mb: 1.5 }}
          >
            <Autocomplete<ZoneOption, false, true, false>
              options={zoneOptions}
              value={selectedZone}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o.label}
              disableClearable
              size="small"
              sx={{ width: 340 }}
              onChange={(_e, o) => o && handleZoneChange(o.id)}
              renderInput={(params) => <TextField {...params} label="Timezone" />}
            />
            <Button size="small" variant="text" onClick={() => setAnchorEl(null)}>
              OK
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {/* flexShrink:0 keeps the calendar at its natural width so day
                cells stay square (no oval selection ring). */}
            <DateCalendar
              value={pickerValue}
              onChange={handleChange}
              referenceDate={referenceDate}
              sx={{ flexShrink: 0 }}
            />
            <MultiSectionDigitalClock
              value={pickerValue}
              onChange={handleChange}
              referenceDate={referenceDate}
              ampm
              timeSteps={{ minutes: 5 }}
              sx={{ flexShrink: 0, maxHeight: 280 }}
            />
          </Stack>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}
